import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { KeyRound, ArrowRight } from "lucide-react";
import { MaskedLine, Reveal, Overline } from "@/components/Reveal";
import { API, trackEvent, formatApiError } from "@/lib/api";

export default function Redeem() {
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get("code") || "");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Redeem Your Membership — Wired 2 Win Golf";
    trackEvent("page_view", { page: "redeem" });
  }, []);

  const redeem = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      await axios.post(`${API}/redeem`, { code, email });
      trackEvent("code_redeemed");
      navigate(`/members?email=${encodeURIComponent(email)}`);
    } catch (err) {
      setStatus("idle");
      setError(formatApiError(err));
      trackEvent("code_redeem_failed");
    }
  };

  return (
    <main data-testid="redeem-page" className="pt-32 lg:pt-40 min-h-[100svh]">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-28">
        <Overline className="mb-6"><MaskedLine>Partner access</MaskedLine></Overline>
        <h1 className="font-display font-black tracking-tighter leading-[0.92] text-5xl sm:text-7xl uppercase max-w-4xl">
          <MaskedLine delay={0.15}>Your membership</MaskedLine>
          <MaskedLine delay={0.3}><span className="text-volt">is on us.</span></MaskedLine>
        </h1>
        <Reveal delay={0.45}>
          <p className="mt-8 max-w-xl text-white/60 text-base sm:text-lg leading-relaxed">
            If you received a Wired2Win access code through one of our partner organizations, enter it below to unlock your full membership — no payment needed.
          </p>
        </Reveal>

        <Reveal delay={0.55}>
          <form onSubmit={redeem} className="mt-12 max-w-md border border-volt/40 bg-volt/5 p-8 sm:p-10" data-testid="redeem-form">
            <KeyRound className="w-7 h-7 text-volt mb-6" />
            <div className="space-y-4">
              <div>
                <label htmlFor="redeem-code" className="block text-xs font-bold uppercase tracking-widest text-white/50 mb-2">Access code</label>
                <input
                  id="redeem-code"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="W2W-XXXX-XXXXXX"
                  data-testid="redeem-code-input"
                  className="w-full bg-white/5 border border-white/15 px-4 py-3.5 text-sm font-mono tracking-widest placeholder:text-white/30 focus:outline-none focus:border-volt transition-colors uppercase"
                />
              </div>
              <div>
                <label htmlFor="redeem-email" className="block text-xs font-bold uppercase tracking-widest text-white/50 mb-2">Your email</label>
                <input
                  id="redeem-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  data-testid="redeem-email-input"
                  className="w-full bg-white/5 border border-white/15 px-4 py-3.5 text-sm placeholder:text-white/30 focus:outline-none focus:border-volt transition-colors"
                />
              </div>
            </div>
            {error && <p data-testid="redeem-error" className="text-red-400 text-xs mt-4">{error}</p>}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={status === "loading"}
              data-testid="redeem-submit-button"
              className="mt-6 w-full bg-volt text-obsidian font-display font-bold px-6 py-4 hover:bg-white transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {status === "loading" ? "Unlocking..." : "Unlock My Membership"} <ArrowRight className="w-4 h-4" />
            </motion.button>
            <p className="mt-4 text-[11px] text-white/30 leading-relaxed">
              One code per golfer. Questions? Email dbisbee@egolfgroup.com.
            </p>
          </form>
        </Reveal>
      </section>
    </main>
  );
}
